import { Divider } from '@material-ui/core';
import cx from 'classnames';
import PropTypes from 'prop-types';

import { CustomTooltip } from 'src/components/CustomTooltip';
import { CustomTypography, TYPOGRAPHY_VARIANT } from 'src/components/CustomTypography';
import { ErrorMessage } from 'src/components/ErrorMessage';
import { GenerationModeList } from 'src/components/GenerationModeList';
import { StaticPanel } from 'src/components/StaticPanel';
import { formatModeListId, getCasingValue, isSameLanguage, LANGUAGE } from 'src/data/generation';
import { isNonEnglish } from 'src/data/generation';
import { NON_CAPITALIZED_LANGUAGES } from 'src/data/generation';
import {
  doesToolSupportMixtureModes,
  getDefaultCharsByTool,
  getToolGenerationModes,
  isFreestyleGenerationTool,
} from 'src/data/generationTool';
import { filterVisibleDDTemplates } from 'src/data/utils';

import { useBrandVoices } from '../../services';

import { FieldCases } from './components/FieldCases';
import { FieldMaxChars } from './components/FieldMaxChars';
import { useHandlers } from './useHandlers';
import { usePreparedModes } from './usePreparedModes';
import { useSelectInitialTemplate } from './useSelectInitialTemplate';
import { useStyles } from './useStyles';

export const BlockCustomFormula = ({
  className,
  title,
  classes,
  isRecomposeTool,
  isCustomFormulasVisible,
  subGenerationTool,
  onSelect,
  maxChars,
  onMaxCharsChange,
  displayMaxChars,
  disabled,
  generationTool,
  subGenerationToolsProps,
  selectedModeId,
  selectedModeType,
  errors,
  brandVoicesAssets,
  generating,
  casing,
  onChangeCasing,
  onMenuClose,
  onCasesMenuOpen,
  onCasesMenuClose,
  language,
}) => {
  const styles = useStyles({ classes });

  const availableTemplates = getToolGenerationModes(generationTool);
  const visibleDDTemplates = filterVisibleDDTemplates(availableTemplates);

  const isMixtureModesSupported = doesToolSupportMixtureModes(generationTool);
  const currentTool = isMixtureModesSupported ? subGenerationTool : generationTool;
  const subToolProps = subGenerationToolsProps?.[currentTool];

  const isEnglish = !isNonEnglish(language);
  const showCapitalization = !NON_CAPITALIZED_LANGUAGES.includes(language);

  const { brandVoices } = useBrandVoices();
  const englishBrandVoice = brandVoices.find((bv) => isSameLanguage(bv.language, LANGUAGE.en_us));

  const currentVisibleDDTemplates = isMixtureModesSupported
    ? subToolProps?.visibleDDTemplates
    : visibleDDTemplates;
  const currentSelectedModeId = isMixtureModesSupported
    ? subToolProps?.selectedModeId
    : selectedModeId;
  const currentSelectedModeType = isMixtureModesSupported
    ? subToolProps?.selectedModeType
    : selectedModeType;
  const currentToolMaxChars = isMixtureModesSupported ? subToolProps?.maxChars : maxChars;
  const currentCasing = isMixtureModesSupported ? subToolProps?.casing : casing;
  const currentErrors = isMixtureModesSupported ? subToolProps?.errors : errors;
  const errorMessage = isMixtureModesSupported
    ? currentErrors?.errorAlert || currentErrors?.maxChars
    : currentErrors?.formula || currentErrors?.maxChars;
  const { modes, preparedBrandVoicesAssets } = usePreparedModes(
    currentTool,
    currentVisibleDDTemplates,
    brandVoicesAssets
  );
  const flattenFormulas = preparedBrandVoicesAssets.reduce(
    (acc, { customFormulas }) => [...acc, ...customFormulas],
    []
  );
  const { handleAddFormula, handleSubGenerationToolAddFormula } = useHandlers({
    generationTool: currentTool,
    onSelect,
    onMenuClose,
    subGenerationTool,
  });
  // Preselect first mode on "mode group" activation
  useSelectInitialTemplate(
    [...modes, ...flattenFormulas],
    isMixtureModesSupported,
    currentTool,
    currentSelectedModeId,
    currentSelectedModeType,
    onSelect
  );

  // We use formatted ID, because we can have modes with same real ID's but of different types
  const selectedListId = formatModeListId(currentSelectedModeId, currentSelectedModeType);

  const defaultMinMaxChars =
    getDefaultCharsByTool(currentTool) || isFreestyleGenerationTool(currentTool);

  return (
    <StaticPanel
      className={cx(styles.root, className)}
      title={title}
      classes={classes}
    >
      <CustomTooltip title={disabled ? 'Formulas are disabled when using a custom model' : ''}>
        <div>
          <div className={styles.content}>
            {displayMaxChars && !!defaultMinMaxChars && (
              <div className={styles.maxCharsWrapper}>
                <CustomTypography variant={TYPOGRAPHY_VARIANT.medium14}>
                  Max characters
                </CustomTypography>
                <FieldMaxChars
                  className={styles.fieldMaxChars}
                  value={currentToolMaxChars}
                  onChange={(value) => onMaxCharsChange(value, currentTool)}
                  error={currentErrors?.maxChars}
                  disabled={disabled || generating}
                />
              </div>
            )}
            {showCapitalization && (
              <FieldCases
                casing={currentCasing}
                onChange={(event) => {
                  const value = getCasingValue(event.currentTarget.innerText);
                  onChangeCasing(value, currentTool);
                }}
                onOpen={onCasesMenuOpen}
                onClose={onCasesMenuClose}
              />
            )}
          </div>
          {isEnglish && (
            <>
              <Divider className={styles.divider} />
              <StaticPanel
                title="Formula"
                className={styles.formulaWrapper}
              >
                <GenerationModeList
                  className={styles.list}
                  modes={modes}
                  selectedId={selectedListId}
                  onSelect={(value) => onSelect(value, currentTool)}
                  generationTool={currentTool}
                  disabled={generating || disabled}
                  isRecomposeTool={isRecomposeTool}
                  brandVoicesAssets={preparedBrandVoicesAssets}
                  isCustomFormulasVisible={isCustomFormulasVisible}
                  onAddCustomFormula={
                    isMixtureModesSupported ? handleSubGenerationToolAddFormula : handleAddFormula
                  }
                  supportsCampaignKeywords
                  error={currentErrors?.formula}
                  hideCreateNew={!englishBrandVoice?.id}
                />
              </StaticPanel>
            </>
          )}
        </div>
      </CustomTooltip>

      <ErrorMessage
        className={styles.error}
        error={errorMessage}
      />
    </StaticPanel>
  );
};

BlockCustomFormula.propTypes = {
  className: PropTypes.string,
  title: PropTypes.string,
  classes: PropTypes.shape({}),
  isRecomposeTool: PropTypes.bool,
  isCustomFormulasVisible: PropTypes.bool,
  displayMaxChars: PropTypes.bool,
  subGenerationTool: PropTypes.string,
  onMaxCharsChange: PropTypes.func,
  disabled: PropTypes.bool,
  language: PropTypes.string,
};
BlockCustomFormula.defaultProps = {
  className: null,
  title: null,
  classes: null,
  isRecomposeTool: false,
  isCustomFormulasVisible: false,
  displayMaxChars: false,
  subGenerationTool: '',
  disabled: false,
  language: null,
};
